import { useCallback, useEffect, useState } from 'react';
import { CONFIG } from '../config/Config';
import {
  Algorithm,
  AlgorithmVersion,
  Client,
  Inbox,
  InboxFileRow,
  Inference,
  InferenceFile,
  Model,
  Pagination,
  Project,
} from '../types';
import { Dataset, DatasetFileVersion, DatasetVersion } from '../types/dataset';
import { Flow } from '../types/flow';
import {
  getAlgorithm,
  getAlgorithmVersionById,
  getAlgorithmVersions,
  getAlgorithms,
  getLatestAlgorithmVersion,
  getModel,
  getModelsForAlgorithmVersion,
  getPaginatedModels,
} from './api/algorithm-api';
import { getClients, getInferenceList, getProject, getProjects } from './api/aws-api';
import { getDataset, getDatasetsByProject, getPaginatedDatasetFiles } from './api/dataset-api';
import { getFlowInferenceList, getFlowsForAlgorithm } from './api/flow-api';
import { getClientInboxFileList, getClientInboxes, getInboxFlowOptions } from './api/inbox-api';
import { getAlgorithmPublic } from './api/public-api';
import { slugify } from './util';

export const useGetAlgorithm = (id: string, open?: boolean) => {
  const [algorithm, setAlgorithm] = useState<Algorithm | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!open) {
        const algorithm = await getAlgorithm(id);
        setAlgorithm(algorithm || null);
      } else {
        const algorithm = await getAlgorithmPublic(id);
        setAlgorithm(algorithm || null);
      }
    };
    fetchData();
    return () => {
      setAlgorithm(null);
    };
  }, [id, open]);

  return {
    algorithm,
  };
};

export const useGetAlgorithms = () => {
  const [algorithms, setAlgorithms] = useState<Algorithm[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const algorithms = await getAlgorithms();
      const configuredAlgorithms = CONFIG.algorithms;
      const selected = configuredAlgorithms.map(it => algorithms.find(cit => `${cit.id}` === `${it.id}`)!);
      setAlgorithms(selected);
    };
    fetchData();
  }, []);

  return { algorithms };
};

const checkHiddenOfAlgorithmVersion = async (algorithmVersionId: string) => {
  const modelsForVersion = await getModelsForAlgorithmVersion(algorithmVersionId);
  const showVersion =
    modelsForVersion.filter(model => model.hidden !== null && model.hidden !== false).length === 0 ? true : false;
  return showVersion;
};

export const useGetAlgorithmAndVersions = (algorithmVersionId: string) => {
  const [algorithm, setAlgorithm] = useState<Algorithm | null>(null);
  const [algorithmVersions, setAlgorithmVersions] = useState<AlgorithmVersion[]>([]);
  const [selectedAlgorithmVersion, setSelectedAlgorithmVersion] = useState<AlgorithmVersion | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (algorithmVersionId) {
        const algorithmVersion = await getAlgorithmVersionById(algorithmVersionId);
        setAlgorithm(algorithmVersion.algorithm || null);
        setSelectedAlgorithmVersion(algorithmVersion);
        const versions = await getAlgorithmVersions(algorithmVersion.algorithm.id);
        const filteredVersions: AlgorithmVersion[] = [];
        await Promise.all(
          versions.map(async version => {
            const check = await checkHiddenOfAlgorithmVersion(version.id);
            if (check && version.version) filteredVersions.push(version);
          })
        );
        const sortedVersions = filteredVersions.sort((a, b) => (a.timeCreated < b.timeCreated ? 1 : -1));
        setAlgorithmVersions(sortedVersions);
      }
    };
    fetchData();
  }, [algorithmVersionId]);

  return { algorithm, algorithmVersions, selectedAlgorithmVersion };
};

export const useGetModels = (algorithmId: string, paginationObject: Pagination, filter: string) => {
  const [models, setModels] = useState<Model[]>([]);
  const [totalItemCount, setTotalItemCount] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      const models = await getPaginatedModels(algorithmId, paginationObject, filter);
      setModels(models.items.filter(model => model.hidden === false || model.hidden === null));
      setTotalItemCount(models.totalItems);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [algorithmId, paginationObject, filter]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { models, totalItemCount, loading, refetch, error };
};

export const useGetModel = (id: string) => {
  const [model, setModel] = useState<Model | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const model = await getModel(id);
      setModel(model || null);
    };
    fetchData();
    return () => {
      setModel(null);
    };
  }, [id]);

  return { model };
};

export const useGetInferenceList = (algorithmId: string, paginationObject: Pagination, filter: string) => {
  const [inferenceList, setInferenceList] = useState<InferenceFile[]>([]);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const refetch = useCallback(
    async (load: boolean, checkActive: any = () => true) => {
      load && setLoading(true);
      if (algorithmId) {
        try {
          const response = await getInferenceList(algorithmId, paginationObject, filter);
          setItemsCount(response.totalItems);
          setInferenceList(response.items);
          setError(false);
        } catch (e) {
          setInferenceList([]);
          setError(true);
        } finally {
          setLoading(false);
        }
      }
    },
    [algorithmId, paginationObject, filter]
  );

  useEffect(() => {
    //define boolean variable to prevent race conditions
    let active: boolean = true;

    const checkActive = () => active;
    refetch(true, checkActive);

    return () => {
      setInferenceList([]);
      setItemsCount(0);
      setLoading(false);
    };
  }, [refetch]);

  return { inferenceList, itemsCount, loading, refetch, error };
};

export const useGetProjectAndDatasets = (projectId: string, paginationObject?: Pagination) => {
  const [project, setProject] = useState<Project | null>();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [totalCount, setTotalCount] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const refetch = useCallback(
    async (checkActive: any = () => true) => {
      if (projectId) {
        try {
          setLoading(true);
          const project = await getProject(projectId);
          const datasets = await getDatasetsByProject(
            projectId,
            true,
            paginationObject && { page: paginationObject.page, pageSize: paginationObject.pageSize }
          );
          if (checkActive()) {
            setProject(project);
            setDatasets(datasets.items);
            setTotalCount(datasets.totalItems);
            setLoading(false);
          }
          setError(false);
        } catch (e) {
          setError(true);
          setProject(null);
          setTotalCount(undefined);
          setDatasets([]);
        } finally {
          setLoading(false);
        }
      }
    },
    [projectId, paginationObject]
  );

  useEffect(() => {
    //define boolean variable to prevent race conditions
    let active: boolean = true;

    const checkActive = () => active;
    refetch(checkActive);

    return () => {
      active = false;
      setProject(null);
      setDatasets([]);
      setTotalCount(undefined);
    };
  }, [refetch]);

  return { project, datasets, totalCount, loading, refetch, error };
};

export const useGetProjectAndDataset = (projectId: string, datasetId: string) => {
  const [project, setProject] = useState<Project | null>();
  const [dataset, setDataset] = useState<Dataset | null>();
  const [datasetVersion, setDatasetVersion] = useState<DatasetVersion>();
  const [loading, setLoading] = useState<boolean>(false);

  const refetch = useCallback(
    async (checkActive: any = () => true) => {
      if (projectId && datasetId) {
        setLoading(true);
        const project = await getProject(projectId);
        const dataset = await getDataset(datasetId);
        const datasetVersion = dataset.latestVersion;
        if (checkActive()) {
          setDataset(dataset);
          setDatasetVersion(datasetVersion);
          setProject(project);
          setLoading(false);
        }
      }
    },
    [datasetId, projectId]
  );

  useEffect(() => {
    //define boolean variable to prevent race conditions
    let active: boolean = true;

    const checkActive = () => active;
    refetch(checkActive);

    return () => {
      active = false;
      setProject(null);
      setDataset(null);
    };
  }, [refetch]);

  return { project, dataset, datasetVersion, loading, refetch };
};

export const useGetDatasetFiles = (
  datasetVersionId: string,
  queryString: string,
  paginationObject: Pagination | null
) => {
  const [datasetFiles, setDatasetFiles] = useState<DatasetFileVersion[]>([]);
  const [filterItemsCount, setFilterItemsCount] = useState<number>(0);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [filesError, setFilesError] = useState<boolean>(false);

  const refetchFiles = useCallback(
    async (load: boolean = false) => {
      load && setFilesLoading(true);
      if (datasetVersionId && paginationObject) {
        try {
          const response = await getPaginatedDatasetFiles(datasetVersionId, queryString, paginationObject);
          setDatasetFiles(response.items);
          setFilterItemsCount(response.totalItems);
          setFilesError(false);
        } catch (e) {
          setFilesError(true);
          setDatasetFiles([]);
        } finally {
          setFilesLoading(false);
        }
      }
    },
    [datasetVersionId, queryString, paginationObject]
  );

  useEffect(() => {
    refetchFiles(true);
  }, [refetchFiles]);

  return { datasetFiles, filterItemsCount, filesLoading, refetchFiles, filesError };
};

export const useGetClientAndInboxes = (clientName: string, doctype?: string) => {
  const [client, setClient] = useState<Client>();
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [inbox, setInbox] = useState<Inbox>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const refetch = useCallback(
    async (checkActive: any = () => true) => {
      try {
        setLoading(true);
        if (clientName) {
          const clients = await getClients();
          const client = clients.find((client: Client) => {
            return slugify(client.io_customer_name) === clientName;
          })!;
          checkActive() && setClient(client);
          if (client) {
            const inboxes = await getClientInboxes(client.io_customer_name);
            const filteredInboxes = inboxes.filter(inbox => inbox.document_class !== null);
            checkActive() && setInboxes(filteredInboxes);
            if (doctype) {
              const currentInbox = inboxes.find((inbox: Inbox) => inbox.document_class === doctype)!;
              checkActive() && setInbox(currentInbox);
            }
          }
        }
        setError(false);
      } catch (e) {
        setInboxes([]);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [clientName, doctype]
  );

  useEffect(() => {
    let active: boolean = true;
    const checkActive = () => active;

    refetch(checkActive);

    return () => {
      active = false;
    };
  }, [refetch]);

  return { client, inboxes, inbox, loading, refetch, error };
};

export const useGetInboxFileList = (
  clientName: string,
  paginationObject: Pagination | null,
  doctype: string,
  queryString: string
) => {
  const [inboxFiles, setInboxFiles] = useState<InboxFileRow[]>([]);
  const [filterItemsCount, setFilterItemsCount] = useState<number>(0);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [filesError, setFilesError] = useState<boolean>(false);

  const refetchFiles = useCallback(
    async (load: boolean = true) => {
      try {
        load && setFilesLoading(true);
        if (clientName && doctype && paginationObject) {
          const response = await getClientInboxFileList(clientName, paginationObject, doctype, queryString);
          setInboxFiles(response.datasetInboxes);
          setFilterItemsCount(response.total_count);
          setFilesError(false);
        }
      } catch (e) {
        setFilesError(true);
        setInboxFiles([]);
      } finally {
        setFilesLoading(false);
      }
    },
    [clientName, paginationObject, doctype, queryString]
  );

  useEffect(() => {
    refetchFiles(true);
  }, [refetchFiles]);

  return { inboxFiles, filterItemsCount, filesLoading, refetchFiles, filesError };
};

export const useGetFlows = (clientName: string) => {
  const [flowOptions, setFlowOptions] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const flows = await getInboxFlowOptions(clientName);
      setFlowOptions(flows);
    };
    fetchData();
  }, [clientName]);

  return { flowOptions };
};

export const useGetFlowsForAlgorithm = (algorithmId: number, paginationObject: Pagination) => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const refetch = useCallback(
    async (checkActive: any = () => true) => {
      try {
        setLoading(true);
        if (algorithmId) {
          const { items, totalItems } = await getFlowsForAlgorithm(algorithmId, paginationObject);
          if (checkActive()) {
            setFlows(items);
            setTotalItemCount(totalItems);
          }
        }
        setError(false);
      } catch (e) {
        setError(true);
        setFlows([]);
      } finally {
        setLoading(false);
      }
    },
    [algorithmId, paginationObject]
  );

  useEffect(() => {
    let active: boolean = true;
    const checkActive = () => active;

    refetch(checkActive);

    return () => {
      active = false;
      setFlows([]);
    };
  }, [refetch]);

  return { flows, totalItemCount, loading, refetch, error };
};

export const useGetFlowInferenceList = (flowId: string, params: Pagination, filter?: string) => {
  const [receivedData, setInferences] = useState<Inference[]>([]);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      if (flowId) {
        const response = await getFlowInferenceList(flowId, params, filter);
        setInferences(response.items);
        setTotalItemCount(response.totalItems);
      }
      setError(false);
    } catch (e) {
      setInferences([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [flowId, params, filter]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { receivedData, totalItemCount, loading, refetch, error };
};

export const useGetModelVersion = (datasetVersion: DatasetVersion) => {
  const [modelId, setModelId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (datasetVersion) {
        if (datasetVersion?.settings?.algorithmId !== '-1' && datasetVersion?.settings?.algorithmId !== undefined) {
          console.log('Use inference with algorithm id: ' + datasetVersion.settings.algorithmId);
          const algorithmVersion = await getLatestAlgorithmVersion(datasetVersion.settings.algorithmId);
          if (algorithmVersion) {
            console.log('Use inference with algorithm version: ' + algorithmVersion.id);
            const models = await getModelsForAlgorithmVersion(algorithmVersion.id);
            const pretrainedModels = models.filter(model => !model.lastModelVersionId && model.hidden === false);
            if (pretrainedModels.length > 0) {
              setModelId(pretrainedModels[0].id);
              console.log('Using inference with model id: ' + pretrainedModels[0].id);
            }
          }
        }
      }
    };
    fetchData();
  }, [datasetVersion]);

  return { modelId };
};

export const useAllDatasets = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const dofetch = useCallback(async (checkActive: any = () => true) => {
    setLoading(true);
    const projects = await getProjects();
    let allDatasets: Dataset[] = [];
    for (let i = 0; i < projects.length; i++) {
      const projectDatasets = await getDatasetsByProject(projects[i].id);
      allDatasets = [...allDatasets, ...projectDatasets.items];
    }

    if (checkActive()) {
      setDatasets(allDatasets);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    //define boolean variable to prevent race conditions
    let active: boolean = true;

    const checkActive = () => active;
    dofetch(checkActive);

    return () => {
      active = false;
      setDatasets([]);
    };
  }, [dofetch]);

  return { datasets, loading, dofetch };
};
